import React, { useState, useEffect, useCallback } from 'react';
import { Settings, Save, Loader2, User, X, CheckCircle2, Bandage, Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${window.__WR_API_BASE__}/api`;

/**
 * PatientProfileSettings — Allows patients to save/load their personal info
 * so it auto-fills into letters. Requires login.
 *
 * v6.8.47 — Added Saved Medical History / Injuries editor + listens for
 * `writeright:prefill-injury-draft` event to pre-fill the draft label
 * after the post-generation nudge.
 */
const PatientProfileSettings = ({ 
  open, onClose, user,
  // Form state setters to auto-fill the wizard
  setSoldierFirstName, setSoldierLastName, setSoldierDob,
  setSoldierRank, setSoldierUnit, setOccupation, setJobDuties,
  setSoldierPhone, setPatientStatus,
}) => {
  const [profile, setProfile] = useState({
    first_name: '', last_name: '', dob: '', rank: '', unit: '',
    occupation: '', job_duties: '', address: '', phone: '',
    patient_status: '', mtf_name: '', saved_injuries: [],
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  // v6.8.47 — Saved Injuries inline editor state.
  const [injDraftLabel, setInjDraftLabel] = useState('');
  const [injDraftYear, setInjDraftYear] = useState('');

  // Load profile on open
  useEffect(() => {
    if (open && user) {
      setLoading(true);
      axios.get(`${API}/patient/profile`, { withCredentials: true })
        .then(res => {
          const data = res.data;
          setProfile(prev => ({
            ...prev,
            first_name: data.first_name || '',
            last_name: data.last_name || '',
            dob: data.dob || '',
            rank: data.rank || '',
            unit: data.unit || '',
            occupation: data.occupation || '',
            job_duties: data.job_duties || '',
            address: data.address || '',
            phone: data.phone || '',
            patient_status: data.patient_status || '',
            mtf_name: data.mtf_name || '',
            saved_injuries: Array.isArray(data.saved_injuries) ? data.saved_injuries : [],
          }));
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [open, user]);

  // v6.8.47 — Listen for the post-generation "save to history" nudge.
  // Pre-fills the draftLabel and opens the modal.
  useEffect(() => {
    const handler = (e) => {
      const lbl = e?.detail?.draftLabel || '';
      if (lbl) setInjDraftLabel(lbl.slice(0, 140));
    };
    window.addEventListener('writeright:prefill-injury-draft', handler);
    return () => window.removeEventListener('writeright:prefill-injury-draft', handler);
  }, []);

  const addInjury = useCallback(() => {
    const label = injDraftLabel.trim();
    if (!label) return;
    setProfile(prev => ({
      ...prev,
      saved_injuries: [
        ...(prev.saved_injuries || []),
        {
          id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `inj-${Date.now()}`,
          label: label.slice(0, 140),
          year: injDraftYear.trim().slice(0, 10),
          active: true,
        },
      ],
    }));
    setInjDraftLabel('');
    setInjDraftYear('');
    setSaved(false);
  }, [injDraftLabel, injDraftYear]);

  const toggleInjuryActive = (id) => {
    setProfile(prev => ({
      ...prev,
      saved_injuries: (prev.saved_injuries || []).map(i => i.id === id ? { ...i, active: !i.active } : i),
    }));
    setSaved(false);
  };
  const removeInjury = (id) => {
    setProfile(prev => ({
      ...prev,
      saved_injuries: (prev.saved_injuries || []).filter(i => i.id !== id),
    }));
    setSaved(false);
  };

  const handleChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
    setSaved(false);
  };

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/patient/profile`, profile, { withCredentials: true });
      toast.success('Profile saved!');
      setSaved(true);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please sign in to save your profile');
      } else {
        toast.error('Failed to save profile');
      }
    } finally {
      setSaving(false);
    }
  }, [profile]);

  const handleApplyToForm = useCallback(() => {
    if (profile.first_name) setSoldierFirstName(profile.first_name);
    if (profile.last_name) setSoldierLastName(profile.last_name);
    if (profile.dob) setSoldierDob(profile.dob);
    if (profile.rank) setSoldierRank(profile.rank);
    if (profile.unit) setSoldierUnit(profile.unit);
    if (profile.occupation) setOccupation(profile.occupation);
    if (profile.job_duties) setJobDuties(profile.job_duties);
    if (profile.phone) setSoldierPhone(profile.phone);
    if (profile.patient_status) setPatientStatus(profile.patient_status);
    toast.success('Profile applied to form!');
    onClose();
  }, [profile, setSoldierFirstName, setSoldierLastName, setSoldierDob, setSoldierRank,
      setSoldierUnit, setOccupation, setJobDuties, setSoldierPhone, setPatientStatus, onClose]);

  if (!open) return null;

  const FIELDS = [
    { key: 'first_name', label: 'First Name', placeholder: 'John' },
    { key: 'last_name', label: 'Last Name', placeholder: 'Smith' },
    { key: 'dob', label: 'Date of Birth', placeholder: 'MM/DD/YYYY', type: 'date' },
    { key: 'rank', label: 'Rank', placeholder: 'SGT, SPC, CPT...' },
    { key: 'unit', label: 'Unit', placeholder: '1-502 IN, 3BCT, 101st ABN...' },
    { key: 'occupation', label: 'Occupation / MOS', placeholder: '11B Infantryman' },
    { key: 'job_duties', label: 'Job Duties', placeholder: 'Primary duties and physical requirements...' },
    { key: 'address', label: 'Address', placeholder: 'Street, City, State ZIP' },
    { key: 'phone', label: 'Phone', placeholder: '(555) 123-4567' },
    { key: 'patient_status', label: 'Status', placeholder: 'Active Duty, Dependent, Civilian...' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="patient-profile-overlay">
      <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto border-border bg-card shadow-xl mx-4" data-testid="patient-profile-modal">
        <CardHeader className="flex flex-row items-center justify-between p-4 pb-3 border-b border-border/50 sticky top-0 bg-card z-10">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <User className="w-5 h-5 text-primary" />
            My Profile
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0" data-testid="close-profile-btn">
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent className="p-4 space-y-3">
          {!user && (
            <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg text-sm text-amber-700">
              Sign in to save your profile across sessions.
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                {FIELDS.map(f => (
                  <div key={f.key} className={f.key === 'job_duties' || f.key === 'address' ? 'col-span-2' : ''}>
                    <Label className="text-xs text-muted-foreground mb-1 block">{f.label}</Label>
                    {f.key === 'job_duties' ? (
                      <textarea
                        value={profile[f.key]}
                        onChange={e => handleChange(f.key, e.target.value)}
                        placeholder={f.placeholder}
                        rows={2}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        data-testid={`profile-${f.key}`}
                      />
                    ) : (
                      <Input
                        type={f.type || 'text'}
                        value={profile[f.key]}
                        onChange={e => handleChange(f.key, e.target.value)}
                        placeholder={f.placeholder}
                        className="h-9 text-sm"
                        data-testid={`profile-${f.key}`}
                      />
                    )}
                  </div>
                ))}
              </div>

              {/* v6.8.47 — Saved Medical History / Injuries.
                  Persisted server-side via the same /api/patient/profile
                  PUT (saved_injuries field). Auto-injected into SSA/LTD/
                  IDES/Buddy/JOES letters via /api/generate. */}
              <div className="space-y-3 p-3 bg-rose-500/5 border border-rose-500/20 rounded-lg" data-testid="patient-saved-injuries-editor">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-sm font-medium text-rose-300">
                    <Bandage className="w-4 h-4" />
                    My Medical History / Injuries
                    <span className="text-[10px] px-2 py-0.5 bg-rose-500/15 text-rose-300 rounded font-bold">DISABILITY HELPER</span>
                  </div>
                  <span className="text-[10px] text-muted-foreground">{(profile.saved_injuries || []).length} saved</span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Save your past conditions with year (e.g., L5-S1 disc herniation, 2022). Active flares auto-inject into any SSA / LTD / IDES / Buddy / JOES letter you generate — no re-typing required. Inactive ones still save but only inject when you tap their chip above the dictation textbox.
                </p>

                {(profile.saved_injuries || []).length > 0 && (
                  <div className="space-y-1.5">
                    {(profile.saved_injuries || []).map((inj) => (
                      <div
                        key={inj.id}
                        className={`flex items-center gap-2 p-2 rounded-md border ${inj.active ? 'border-rose-500/30 bg-rose-500/5' : 'border-border/40 bg-secondary/30 opacity-70'}`}
                        data-testid={`patient-saved-injury-${inj.id}`}
                      >
                        <button
                          type="button"
                          onClick={() => toggleInjuryActive(inj.id)}
                          className={`text-[10px] px-2 py-0.5 rounded-full font-bold shrink-0 ${inj.active ? 'bg-rose-500/20 text-rose-300' : 'bg-muted text-muted-foreground'}`}
                          data-testid={`patient-saved-injury-${inj.id}-toggle`}
                          title="Toggle active flare"
                        >
                          {inj.active ? 'ACTIVE' : 'inactive'}
                        </button>
                        <span className="text-sm text-foreground flex-1 min-w-0 truncate">
                          {inj.label}
                          {inj.year && <span className="text-muted-foreground ml-2 text-xs">({inj.year})</span>}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeInjury(inj.id)}
                          className="text-muted-foreground hover:text-rose-400 transition-colors shrink-0"
                          data-testid={`patient-saved-injury-${inj.id}-remove`}
                          title="Remove"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex items-end gap-2 pt-1">
                  <div className="flex-1 min-w-0 space-y-1">
                    <Label className="text-xs">Condition / Injury</Label>
                    <input
                      value={injDraftLabel}
                      onChange={(e) => setInjDraftLabel(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addInjury(); } }}
                      placeholder="e.g., L5-S1 disc herniation"
                      className="w-full h-9 px-3 rounded-md border border-border bg-input text-sm"
                      data-testid="patient-saved-injury-label-input"
                      maxLength={140}
                    />
                  </div>
                  <div className="w-24 space-y-1">
                    <Label className="text-xs">Year</Label>
                    <input
                      value={injDraftYear}
                      onChange={(e) => setInjDraftYear(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addInjury(); } }}
                      placeholder="2024"
                      className="w-full h-9 px-2 rounded-md border border-border bg-input text-sm"
                      data-testid="patient-saved-injury-year-input"
                      maxLength={10}
                    />
                  </div>
                  <Button
                    type="button"
                    onClick={addInjury}
                    size="sm"
                    disabled={!injDraftLabel.trim()}
                    className="h-9 px-3"
                    data-testid="patient-saved-injury-add-btn"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add
                  </Button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 pt-2 border-t border-border/50">
                {user && (
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    size="sm"
                    className="gap-1.5"
                    data-testid="save-profile-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <CheckCircle2 className="w-4 h-4" /> : <Save className="w-4 h-4" />}
                    {saving ? 'Saving...' : saved ? 'Saved' : 'Save Profile'}
                  </Button>
                )}
                <Button
                  onClick={handleApplyToForm}
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  data-testid="apply-profile-btn"
                >
                  Apply to Form
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    // Shared-device safety valve — wipe all sticky identity
                    // from localStorage AND clear current form so the next
                    // user starts blank. Feb 24, 2026.
                    [
                      'writeright_identity_patient_name',
                      'writeright_identity_patient_status',
                      'writeright_identity_rank',
                      'writeright_identity_first_name',
                      'writeright_identity_last_name',
                      'writeright_identity_dob',
                      'writeright_identity_unit',
                      'writeright_identity_phone',
                      'writeright_identity_mtf',
                      'writeright_identity_sig_block',
                      'writeright_identity_custom_mtf',
                      'writeright_identity_occupation',
                      'writeright_identity_job_duties',
                      'writeright_bracket_fields',
                    ].forEach(k => { try { localStorage.removeItem(k); } catch { /* ignore */ } });
                    if (setSoldierFirstName) setSoldierFirstName('');
                    if (setSoldierLastName) setSoldierLastName('');
                    if (setSoldierDob) setSoldierDob('');
                    if (setSoldierRank) setSoldierRank('');
                    if (setSoldierUnit) setSoldierUnit('');
                    if (setSoldierPhone) setSoldierPhone('');
                    if (setOccupation) setOccupation('');
                    if (setJobDuties) setJobDuties('');
                    if (setPatientStatus) setPatientStatus('');
                    toast.success('Saved identity cleared from this device');
                    onClose();
                  }}
                  className="text-red-400 hover:text-red-300"
                  data-testid="clear-identity-btn"
                  title="Wipe all stored identity from this device (shared device safety)"
                >
                  Clear Saved Info
                </Button>
                <Button variant="ghost" size="sm" onClick={onClose} className="ml-auto">
                  Close
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PatientProfileSettings;
